import { withDependencies } from '@wix/thunderbolt-ioc'
import type { ComponentsInnerScroll, ScrollableNodes, UnsubscribeFromInnerScroll } from './types'
import _ from 'lodash'
import { IComponentScrollApi, InnerScrollApiType, OnComponentsScrollListenerCb } from '@wix/thunderbolt-becky-types'

const innerScrollApiFactory = (): InnerScrollApiType => {
	const componentsInnerScroll: ComponentsInnerScroll = {}
	const scrollableComponents: ScrollableNodes = {}
	const unsubscribeFromScroll: UnsubscribeFromInnerScroll = {}
	let onComponentsScrollListener: OnComponentsScrollListenerCb | undefined

	const setComponentInnerScroll = (compId: string, { x, y }: { x: number; y: number }) => {
		componentsInnerScroll[compId] = {
			x,
			y,
		}
	}

	const setOnComponentsScrollListener = (callback: OnComponentsScrollListenerCb) => {
		if (_.isFunction(callback)) {
			onComponentsScrollListener = callback
		}
	}

	const updateComponentInnerScroll = (compId: string, scrollApi: IComponentScrollApi) => {
		setComponentInnerScroll(compId, {
			x: scrollApi.getScrollLeft(),
			y: scrollApi.getScrollTop(),
		})
	}

	const getScrollListener = (compId: string) => {
		return (event: Event) => {
			const node = event.target! as Element
			setComponentInnerScroll(compId, { x: node.scrollLeft, y: node.scrollTop })

			if (onComponentsScrollListener) {
				onComponentsScrollListener(compId, { left: node.scrollLeft, top: node.scrollTop })
			}
		}
	}

	const getUnregisterFunction = (compId: string) => {
		return () => {
			const scrollableNode = scrollableComponents[compId]

			if (scrollableNode) {
				scrollableNode.scroll(0, 0)
				unsubscribeFromScroll[compId]!()
				unsubscribeFromScroll[compId] = undefined
				componentsInnerScroll[compId] = undefined
				scrollableComponents[compId] = undefined
			}
		}
	}

	return {
		registerToInnerScroll: (compId, scrollApi: IComponentScrollApi) => {
			scrollableComponents[compId] = scrollApi
			updateComponentInnerScroll(compId, scrollApi)

			unsubscribeFromScroll[compId] = scrollApi.onScroll(getScrollListener(compId))

			return getUnregisterFunction(compId)
		},
		scrollTo: (compId, x, y) => {
			const compScrollApi = scrollableComponents[compId]

			if (compScrollApi) {
				compScrollApi.scroll(x, y)

				updateComponentInnerScroll(compId, compScrollApi)
			}
		},
		scrollBy: (compId, x, y) => {
			const compScrollApi = scrollableComponents[compId]

			if (compScrollApi) {
				compScrollApi.scrollBy(x, y)

				updateComponentInnerScroll(compId, compScrollApi)
			}
		},
		isScrollable: (compId) => !!scrollableComponents[compId],
		getComponentInnerScroll: (compId) => componentsInnerScroll[compId],
		setOnComponentsScrollListener,
	}
}

export const InnerScrollApi = withDependencies([], innerScrollApiFactory)
